/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { getFormatParse } from '#UNSAFE_IntlDateTime';
import { TimeComponentScale } from '../timeComponent.types';

const DAY = 1000 * 60 * 60 * 24;

/**
 * Returns the timestamp value of the given ISO date string.
 * @param date The ISO date string.
 * @returns The number of milliseconds that has elapsed since the epoch.
 */
const parseDate = (date: string) => Date.parse(date);

/**
 * Returns the ISO date string given a date timestamp.
 * @param date The timestamp (number of milliseconds that has elapsed since the epoch.)
 * @returns The corresponding ISO date string.
 */
const getISOString = (time: number) => new Date(time).toISOString();

/**
 * Gets the pixel position of the given time in a given viewport.
 * @param time The query time.
 * @param viewportStartTime The viewport start time.
 * @param viewportEndTime The viewport end time.
 * @param width The viewport width.
 * @param isRTL Whether the reading direction is rtl.
 * @returns The pixel position.
 */
const getTimePosition = (
  time: number,
  viewportStartTime: number,
  viewportEndTime: number,
  width: number,
  isRTL: boolean
) => {
  const ltrPos = (width * (time - viewportStartTime)) / (viewportEndTime - viewportStartTime);
  return isRTL ? width - ltrPos : ltrPos;
};

/**
 * Gets the time of a given pixel position in a given viewport.
 * @param pos The query position.
 * @param viewportStartTime Thew viewport start time.
 * @param viewportEndTime The viewport end time.
 * @param width The viewport width.
 * @param isRTL Whether the reading direction is rtl.
 * @returns The time.
 */
const getPositionTime = (
  pos: number,
  viewportStartTime: number,
  viewportEndTime: number,
  width: number,
  isRTL: boolean
) => {
  const ltrPos = isRTL ? width - pos : pos;
  return (ltrPos * (viewportEndTime - viewportStartTime)) / width + viewportStartTime;
};

/**
 * Gets an appropriate date formatter function for the given scale.
 * @param scale The time scale.
 * @param timeTest A time to invoke the scale on to determine the best date formatter function to return.
 * @returns The date formatter function.
 */
const getDefaultDateFormatter = (scale: TimeComponentScale, timeTest: number) => {
  // This is used for default datatip and acc.
  // Note that in legacy, the default converter depends on the enum scale of the minor axis.
  // However, in preact, we don't have enum scales, so we'll instead default based
  // on the duration of a minor axis scale interval, i.e. if interval < 1 day, use datetime formatter,
  // otherwise use date formatter. This should yield the same desired behavior if the default importable scales
  // are used. For custom scales in legacy we just use dateformatter always even when the resolution is in < day.
  // So arguably, this is a better default behavior than what we have in legacy.
  //
  // TODO: Decide whether to change legacy to do this behavior, OR in corepack, to 100%
  // preserve the legacy behavior for default datatips + custom time scales,
  // we'll make sure to pass the appropriate default converter to
  // preact comp's valueformats based on scale (when we support valueformats in the future).
  const timeTestString = getISOString(timeTest);
  const intervalStart = parseDate(scale.getPreviousDate(timeTestString));
  const intervalEnd = parseDate(scale.getNextDate(timeTestString));
  const duration = intervalEnd - intervalStart;
  const formatOptions =
    duration <= DAY
      ? {
          dateStyle: 'medium' as const,
          timeStyle: 'medium' as const,
          locale: navigator.language
        }
      : {
          dateStyle: 'medium' as const,
          locale: navigator.language
        };
  return getFormatParse(formatOptions).format as (date: string) => string;
};

export { parseDate, getISOString, getTimePosition, getPositionTime, getDefaultDateFormatter };
